import { setTrackingId } from "client/Misc/utils";
import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ContactListItem } from "client/Api/Email/contacts";
import { Group } from "./Group/Group";
import {
	groupAlreadySortedContacts,
	IContactGroup,
	ContactGroupingKind
} from "./groups";
import styles from "./List.module.less";
import { LoadMoreIndicator } from "client/Components";
import { AddressBookOverviewLayoutKind } from "../OverviewLayoutKindSelector";
import { AddressBookContactSource } from "../Overview";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { ContactItemIdentifier } from "./ContactItem/ContactItem";

export type ListProps = {
	layoutKind: AddressBookOverviewLayoutKind;
	source: AddressBookContactSource;
	items: ReadonlyArray<ContactListItem>;
	activeItemId: ContactItemIdentifier | null;
	itemsGroupBy: ContactGroupingKind;
	onOpenItem(id: ContactItemIdentifier): void;
	isBusy: boolean;
	hasMoreItems: boolean;
	onRequestMoreItems(): void;
};

export const List = (props: ListProps): ReactElement => {
	const {
		layoutKind,
		source,
		items,
		activeItemId,
		itemsGroupBy,
		onOpenItem,
		hasMoreItems,
		isBusy,
		onRequestMoreItems
	} = props;

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const groups = useMemo(
		(): readonly IContactGroup<ContactListItem>[] => {
			return groupAlreadySortedContacts(
				items,
				itemsGroupBy
			);
		},
		[items, itemsGroupBy]
	);

	const emptyTitle: string = groupByToEmptyGroupTitle[itemsGroupBy];

	return (
		<>
			<ul {...setTrackingId("list")}>
				{groups.map(group => (
					<li key={group.key} className={styles.item}>
						<Group
							title={group.title}
							source={source}
							items={group.items}
							activeItemId={activeItemId}
							onOpenItem={onOpenItem}
							emptyTitle={emptyTitle}
							layoutKind={layoutKind}
							isResponsive={isResponsive}
						/>
					</li>
				))}
			</ul>
			<LoadMoreIndicator
				hasMoreItems={hasMoreItems}
				isLoadingData={isBusy}
				onRequestMoreItems={onRequestMoreItems}
			/>
		</>
	);
};
List.displayName="List";

export const groupByToEmptyGroupTitle: { [key in ContactGroupingKind]: string } = {
	[ContactGroupingKind.FirstLetterFirstName]: "*",
	[ContactGroupingKind.FirstLetterLastName]: "*",
	[ContactGroupingKind.FirstLetterDisplayName]: "*",
	[ContactGroupingKind.JobTitle]: localize("_NoJobTitle"),
	[ContactGroupingKind.Department]: localize("_NoDepartment"),
	[ContactGroupingKind.Company]: localize("_NoCompany"),
	[ContactGroupingKind.OfficeLocation]: localize("_NoLocation")
};